/**
 * Drapeaux ronds des langues de l'application, pour le sélecteur de langue.
 *
 * SVG inline plutôt qu'emoji (🇫🇷) : Windows ne rend pas les emoji drapeaux et
 * affiche le code pays à la place (« GB »), et un emoji ne peut pas être rond.
 *
 * Le cercle est découpé au clipPath, pas en `border-radius` : le clip est porté
 * par le SVG lui-même et ne dépend d'aucune CSS extérieure.
 *
 * Chaque drapeau porte un liseré `currentColor` à 25 % : sans lui, la bande
 * blanche du drapeau français et les croix blanches de l'Union Jack se fondent
 * dans le fond du menu. `currentColor` suit la couleur du texte, donc le liseré
 * fonctionne en thème clair comme en thème sombre.
 */

const FranceFlag = ({ className, ...props }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={className} {...props}>
        <clipPath id="locale-flag-fr">
            <circle cx="12" cy="12" r="12" />
        </clipPath>
        <g clipPath="url(#locale-flag-fr)">
            <rect width="8" height="24" fill="#002654" />
            <rect x="8" width="8" height="24" fill="#FFFFFF" />
            <rect x="16" width="8" height="24" fill="#ED2939" />
        </g>
        <circle cx="12" cy="12" r="11.5" fill="none" stroke="currentColor" strokeOpacity="0.25" />
    </svg>
);

/*
 * L'anglais du produit est britannique (en-GB, cf. resources/lang côté API) :
 * c'est donc l'Union Jack, pas la bannière étoilée.
 *
 * Les diagonales rouges sont centrées sur les blanches. Le vrai pavillon les
 * décale (contre-écartelé) ; à 20 px de côté l'écart vaudrait moins d'un pixel,
 * pour trois fois le balisage.
 */
const UnitedKingdomFlag = ({ className, ...props }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={className} {...props}>
        <clipPath id="locale-flag-gb">
            <circle cx="12" cy="12" r="12" />
        </clipPath>
        <g clipPath="url(#locale-flag-gb)">
            <rect width="24" height="24" fill="#012169" />
            <path d="M0 0 24 24M24 0 0 24" stroke="#FFFFFF" strokeWidth="5" />
            <path d="M0 0 24 24M24 0 0 24" stroke="#C8102E" strokeWidth="2.5" />
            <path d="M12 0V24M0 12H24" stroke="#FFFFFF" strokeWidth="8" />
            <path d="M12 0V24M0 12H24" stroke="#C8102E" strokeWidth="4.8" />
        </g>
        <circle cx="12" cy="12" r="11.5" fill="none" stroke="currentColor" strokeOpacity="0.25" />
    </svg>
);

const SpainFlag = ({ className, ...props }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={className} {...props}>
        <clipPath id="locale-flag-es">
            <circle cx="12" cy="12" r="12" />
        </clipPath>
        <g clipPath="url(#locale-flag-es)">
            <rect width="24" height="6" fill="#AA151B" />
            <rect y="6" width="24" height="12" fill="#F1BF00" />
            <rect y="18" width="24" height="6" fill="#AA151B" />
        </g>
        <circle cx="12" cy="12" r="11.5" fill="none" stroke="currentColor" strokeOpacity="0.25" />
    </svg>
);

/**
 * Une langue n'est pas un pays : l'espagnol se parle bien au-delà de l'Espagne.
 * Le raccourci langue -> drapeau reste la convention la plus lisible pour un
 * sélecteur, à revoir seulement si une variante régionale est ajoutée (es-MX…).
 */
const FLAGS = {
    fr: FranceFlag,
    en: UnitedKingdomFlag,
    es: SpainFlag,
};

/**
 * Drapeau rond de la locale, purement décoratif : le nom de la langue est déjà
 * écrit à côté, d'où l'aria-hidden.
 *
 * @param {{ code: string, className?: string }} props code de locale (fr/en/es)
 */
export function LocaleFlag({ code, className = 'h-4 w-4 shrink-0' }) {
    const Flag = FLAGS[code];
    if (!Flag) return null;
    return <Flag className={className} aria-hidden="true" />;
}
